import { CREDENTIAL_LABEL_SOURCE_GENERATED } from '@shared/schema/credentialLabelSource.js';
import buildProviderSignInSecrets from '@/lib/connections/buildProviderSignInSecrets.js';
import resolveProviderSignInFields from '@/lib/connections/resolveProviderSignInFields.js';
import signsInOnProviderSite from '@/lib/connections/signsInOnProviderSite.js';
import loadAppsCatalog from '@/redux/actions/pieveActions/loadAppsCatalog.js';
import getAppByIdAndVersion from '@/redux/requests/apps/getAppByIdAndVersion.js';
import { getConnections } from '@/redux/requests/connectionsRequests.js';
import getOauthAuthorizeUrl from '@/redux/requests/credentials/getOauthAuthorizeUrl.js';
import upsertCompanyCredential from '@/redux/requests/credentials/upsertCompanyCredential.js';
import { appDefinitionForId, connections } from '@/redux/selectors/pieveSelectors/connectionSelectors.js';
import { appsMetadataItems } from '@/redux/selectors/pieveSelectors/runtimeSelectors.js';

/**
 * What a conversation's Connect button does, ported from the earlier frontend's connection card and
 * connection window.
 *
 * An app that signs in with typed values is connected with the form on its connection page, so the
 * answer is that page. An app that signs in on the provider's site gets a sign-in link: a reconnect
 * repairs the connection the card names; a connect reuses a connection that was started and never
 * signed in, and only then saves a new one, so an abandoned sign-in never leaves a second one behind.
 *
 * Some provider sign-ins need values from the person first (Salesforce's domain, Dynamics 365's
 * tenant): the API builds the provider address from the connection's saved values. A connect reads
 * the app's OAuth 2 sign-in from its definition; when it has visible fields it returns them for the
 * card to ask for (and again while a required one is empty), and once `values` fill them it saves
 * them on the connection (as the earlier frontend did) before asking for the link. A reconnect keeps
 * the values already saved.
 *
 * Returns `{ success: true, data: { kind: 'form', path } }`, `{ kind: 'fields', fields }`,
 * `{ kind: 'oauth', url }`, or `{ success: false, message }`.
 */
const startConnectionSignIn = ({ connectionSetup, values = null, returnPath, returnOrigin }) => {
  return async (dispatch, getState) => {
    const setupApp = connectionSetup?.app || {};
    const findApp = () => appsMetadataItems(getState()).find((app) => (setupApp.id && app?.id === setupApp.id) || (setupApp.slug && app?.slug === setupApp.slug)) || null;
    let app = findApp();

    if (!app) {
      await dispatch(loadAppsCatalog());
      app = findApp();
    }

    const appName = app?.name || setupApp.name || 'this app';

    if (!app?.id || !app?.slug) {
      return { success: false, message: `Pieve could not find ${appName} in the app catalog.` };
    }

    if (!signsInOnProviderSite(app)) {
      return { success: true, data: { kind: 'form', path: `/connections/${encodeURIComponent(app.slug)}` } };
    }

    let credentialId = connectionSetup?.setup?.mode === 'reconnect' ? connectionSetup?.setup?.credentialId || '' : '';

    if (!credentialId) {
      if (!appDefinitionForId(getState(), app.id)) {
        const definitionRes = await getAppByIdAndVersion({ appId: app.id, appVersion: app.publishedVersion || app.version });

        if (definitionRes?.success === false) {
          return { success: false, message: definitionRes.message || `Pieve could not load ${appName}.` };
        }
      }

      const { auth, fields } = resolveProviderSignInFields(appDefinitionForId(getState(), app.id));

      if (!auth) {
        return { success: false, message: `${appName} has no sign-in Pieve can open.` };
      }

      const { secrets, missing } = buildProviderSignInSecrets({ auth, fields, values: values || {}, label: app.name });

      // The person sees the fields before the sign-in (as in the earlier frontend's connection
      // window), and cannot go on while one the definition or the sign-in address needs is empty.
      if ((fields.length > 0 && !values) || missing.length > 0) {
        return { success: true, data: { kind: 'fields', fields: fields.map(({ key, name, description, placeholder, required, isSensitive }) => ({ key, name, description, placeholder, required: required === true, isSensitive: isSensitive === true })) } };
      }

      const listRes = await getConnections();

      if (!Array.isArray(listRes)) {
        return { success: false, message: listRes?.message || 'Your connections could not be loaded.' };
      }

      const draft = connections(getState()).find((connection) => connection?.appId === app.id && !connection?.authenticatedOn && connection?.status !== 'valid');
      const saveRes = await upsertCompanyCredential({
        appId: app.id,
        credentialId: draft?.credentialId || undefined,
        secrets,
        meta: { label: app.name, labelSource: CREDENTIAL_LABEL_SOURCE_GENERATED, type: auth.type, authId: auth.id },
        source: 'pieve-web-connect'
      });
      credentialId = saveRes?.credentialId || draft?.credentialId || '';

      if (saveRes?.success === false || !credentialId) {
        return { success: false, message: saveRes?.message || 'The connection could not be saved.' };
      }
    }

    const linkRes = await getOauthAuthorizeUrl({ appSlug: app.slug, credentialId, returnPath, returnOrigin });

    if (linkRes?.success !== true || !linkRes?.data?.url) {
      return { success: false, message: linkRes?.message || `Pieve could not open the ${appName} sign-in.` };
    }

    return { success: true, data: { kind: 'oauth', url: linkRes.data.url } };
  };
};

export default startConnectionSignIn;
